import React, { useState } from "react";
import { Button, Box } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DownloadIcon from "@mui/icons-material/Download";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Papa from "papaparse";
import { DataGrid } from "@mui/x-data-grid";
import axios from "axios";

export default function UpdateExternalStaffContact() {
  const [previewData, setPreviewData] = useState([]);
  const [csvFile, setCsvFile] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  const user = JSON.parse(sessionStorage.getItem("user"));
  const clientId = user?.client_id;
  const collegeId = user?.college_id;
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  // CSV headers - All updatable columns from tbl_mstr_staff_external
  const csvHeaders = [
    "external_staff_id",
    "salutation",
    "staff_name",
    "email",
    "designation",
    "qualification",
    "aadhar_number",
    "personal_mobile",
    "alternative_mobile",
    "personal_address",
    "college_name",
    "college_address",
    "referenced_by",
    "staff_status",
    "workflow_status",
    "delete_status",
  ];

  // Download CSV Template
  const handleDownloadTemplate = () => {
    const csvContent = csvHeaders.join(",") + "\n";
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "external_staff_update_template.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // DataGrid columns
  const columns = [
    { field: "sno", headerName: "S.No", width: 70 },
    { field: "external_staff_id", headerName: "Staff ID", width: 120 },
    { field: "salutation", headerName: "Salutation", width: 100 },
    { field: "staff_name", headerName: "Staff Name", width: 180 },
    { field: "email", headerName: "Email", width: 200 },
    { field: "designation", headerName: "Designation", width: 150 },
    { field: "qualification", headerName: "Qualification", width: 150 },
    { field: "aadhar_number", headerName: "Aadhar Number", width: 130 },
    { field: "personal_mobile", headerName: "Personal Mobile", width: 130 },
    { field: "alternative_mobile", headerName: "Alternative Mobile", width: 130 },
    { field: "personal_address", headerName: "Personal Address", width: 200 },
    { field: "college_name", headerName: "College Name", width: 180 },
    { field: "college_address", headerName: "College Address", width: 200 },
    { field: "referenced_by", headerName: "Referenced By", width: 150 },
    { field: "staff_status", headerName: "Staff Status", width: 120 },
    { field: "workflow_status", headerName: "Workflow Status", width: 120 },
    { field: "delete_status", headerName: "Delete Status", width: 100 },
    {
      field: "errors",
      headerName: "Errors",
      width: 300,
      renderCell: (params) => (
        <span style={{ color: "red" }}>
          {params.value && params.value.length > 0 ? params.value.join(", ") : ""}
        </span>
      ),
    },
  ];

  // File selection
  const handleFileChange = (e) => {
    setCsvFile(e.target.files[0]);
    setShowPreview(false);
    setPreviewData([]);
  };

  // Parse + validate
  const handlePreview = () => {
    if (!csvFile) {
      alert("Please choose a CSV file first!");
      return;
    }

    Papa.parse(csvFile, {
      header: true,
      skipEmptyLines: true,
      complete: function (results) {
        const validatedData = results.data.map((row, index) => {
          let errors = [];
          // Mandatory field validation
          if (!row.external_staff_id) {
            errors.push("Missing external_staff_id");
          }
          // Email validation
          if (row.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.email)) {
            errors.push("Invalid email format");
          }
          // Mobile validation (10 digits)
          if (row.personal_mobile && !/^\d{10}$/.test(row.personal_mobile)) {
            errors.push("Invalid personal_mobile");
          }
          return { ...row, sno: index + 1, errors };
        });
        setPreviewData(validatedData);
        setShowPreview(true);
      },
    });
  };

  // Upload CSV
  const handleUpload = async () => {
    if (!csvFile) return;
    const formData = new FormData();
    formData.append("file", csvFile);
    formData.append("client_id", clientId);
    formData.append("college_id", collegeId);
    try {
      await axios.post(`${API_BASE_URL}/external-staff/bulk-update`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      alert("External staff contact details updated successfully.");
    } catch (error) {
      alert("Failed to update external staff contact details.");
    }
  };

  return (
    <Box p={3}>
      <Box display="flex" flexDirection="row" gap={3} mb={3}>
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={handleDownloadTemplate}
          sx={{
            backgroundColor: "#388e3c",
            color: "#fff",
            fontWeight: "bold",
            minWidth: 180,
            "&:hover": { backgroundColor: "#2c6b2f" },
          }}
        >
          Download CSV
        </Button>
        <input
          type="file"
          accept=".csv"
          style={{ display: "none" }}
          id="csv-upload"
          onChange={handleFileChange}
        />
        <label htmlFor="csv-upload">
          <Button
            variant="contained"
            component="span"
            startIcon={<CloudUploadIcon />}
            sx={{
              backgroundColor: "#1976d2",
              color: "#fff",
              fontWeight: "bold",
              minWidth: 180,
              ml: 2,
              "&:hover": { backgroundColor: "#115293" },
            }}
          >
            Choose CSV
          </Button>
        </label>
        <Button
          variant="contained"
          startIcon={<VisibilityIcon />}
          onClick={handlePreview}
          disabled={!csvFile}
          sx={{
            backgroundColor: "#ff9800",
            color: "#fff",
            fontWeight: "bold",
            minWidth: 140,
            ml: 2,
            "&:hover": { backgroundColor: "#c77700" },
          }}
        >
          Preview
        </Button>
      </Box>
      {showPreview && previewData.length > 0 && (
        <Box mt={2} sx={{ height: 500, width: "100%" }}>
          <DataGrid
            rows={previewData}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10, 20, 50]}
            getRowId={(row) => row.sno}
          />
          <Button
            variant="contained"
            onClick={handleUpload}
            sx={{
              mt: 2,
              backgroundColor: "#4caf50",
              color: "#fff",
              fontWeight: "bold",
              "&:hover": { backgroundColor: "#357a38" },
            }}
          >
            Confirm & Update
          </Button>
        </Box>
      )}
    </Box>
  );
}
